import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type { Analytics } from "../../api/types";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { money } from "../../ui/format";

type Row = { id: string; count: number; revenue: number };

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const PRESETS = [
  { id: "today", title: "Сегодня", days: 0 },
  { id: "week", title: "7 дней", days: 6 },
  { id: "month", title: "30 дней", days: 29 },
  { id: "quarter", title: "90 дней", days: 89 }
] as const;

export function AnalyticsPage() {
  const { notify } = useToasts();

  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 29 * 86400000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    // Верхняя граница — конец выбранного дня, иначе сегодняшние заказы выпадают.
    const params = new URLSearchParams({ from: `${from}T00:00:00`, to: `${to}T23:59:59` });
    return params.toString();
  }, [from, to]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .get<Analytics>(`/api/admin/analytics?${query}`)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(error => {
        if (!cancelled) notify(error instanceof Error ? error.message : "Не удалось загрузить аналитику", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, notify]);

  function applyPreset(days: number) {
    setFrom(isoDay(new Date(Date.now() - days * 86400000)));
    setTo(isoDay(new Date()));
  }

  const peakHour = data?.byHour.reduce<Row | null>(
    (best, row) => (!best || row.count > best.count ? row : best),
    null
  );

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Отчёты</p>
          <h1>Аналитика</h1>
        </div>
        <a className="btn btn-sm" href={`/api/admin/analytics/export.csv?${query}`} download>
          Выгрузить сводку
        </a>
      </div>

      <section className="panel">
        <div className="filters">
          {PRESETS.map(preset => (
            <button type="button" className="btn btn-sm" key={preset.id} onClick={() => applyPreset(preset.days)}>
              {preset.title}
            </button>
          ))}
          <label className="field">
            <span>С</span>
            <input type="date" value={from} onChange={event => setFrom(event.target.value)} />
          </label>
          <label className="field">
            <span>По</span>
            <input type="date" value={to} onChange={event => setTo(event.target.value)} />
          </label>
          <a className="btn btn-sm" href={`/api/admin/orders/export.csv?${query}`} download>
            Выгрузить заказы
          </a>
        </div>
      </section>

      {loading && !data ? <Empty>Считаем…</Empty> : null}

      {data ? (
        <>
          <div className="metrics">
            <div className="metric">
              <b className="num">{money(data.totals.revenue)}</b>
              <span>Выручка за вычетом возвратов</span>
            </div>
            <div className="metric">
              <b className="num">{data.totals.paidOrders}</b>
              <span>Оплаченных заказов</span>
            </div>
            <div className="metric">
              <b className="num">{money(data.totals.averageCheck)}</b>
              <span>Средний чек</span>
            </div>
            <div className="metric">
              <b className="num">{money(data.totals.refunded)}</b>
              <span>Возвращено</span>
            </div>
            <div className="metric">
              <b className="num">{data.totals.cancelledOrders}</b>
              <span>Отменено</span>
            </div>
            <div className="metric">
              <b className="num">{data.totals.unpaidOrders}</b>
              <span>Не дошли до оплаты</span>
            </div>
          </div>

          <section className="panel">
            <header>
              <div>
                <h2>Среднее время</h2>
                <p>
                  По {data.timings.measuredOrders} доставленным заказам. Заказы без полного пути в расчёт не берутся.
                </p>
              </div>
            </header>
            <div className="metrics">
              <div className="metric">
                <b className="num">{formatMinutes(data.timings.acceptMinutes)}</b>
                <span>Принятие</span>
              </div>
              <div className="metric">
                <b className="num">{formatMinutes(data.timings.cookMinutes)}</b>
                <span>Приготовление</span>
              </div>
              <div className="metric">
                <b className="num">{formatMinutes(data.timings.deliveryMinutes)}</b>
                <span>Доставка</span>
              </div>
              <div className="metric">
                <b className="num">{formatMinutes(data.timings.totalMinutes)}</b>
                <span>От оплаты до клиента</span>
              </div>
            </div>
          </section>

          <section className="panel">
            <header>
              <div>
                <h2>Нагрузка по часам</h2>
                {peakHour ? <p>Пик приходится на {peakHour.id}:00 — {peakHour.count} зак.</p> : null}
              </div>
            </header>
            <HourChart rows={data.byHour} />
          </section>

          <div className="admin-two-col">
            <RowTable title="Выручка по дням" header="Дата" rows={data.byDay} />
            <RowTable title="По лавочкам" header="Лавочка" rows={data.byBench} />
          </div>

          <div className="admin-two-col">
            <RowTable title="Популярные товары" header="Товар" rows={data.topProducts} />
            <RowTable title="Популярные добавки" header="Добавка" rows={data.topModifiers} />
          </div>

          <div className="admin-two-col">
            <RowTable title="Промокоды" header="Код" rows={data.promoCodes} amountTitle="Скидка" />
            <RowTable title="Акции" header="Акция" rows={data.promotions} amountTitle="Скидка" />
          </div>

          <RowTable title="Доставщики" header="Сотрудник" rows={data.byCourier} />
        </>
      ) : null}
    </>
  );
}

function formatMinutes(value: number | null): string {
  return value === null ? "—" : `${value} мин`;
}

/** Гистограмма нагрузки: столбики строятся долей от самого загруженного часа. */
function HourChart({ rows }: { rows: Row[] }) {
  if (rows.length === 0) return <Empty>Оплаченных заказов за период нет.</Empty>;

  const peak = Math.max(...rows.map(row => row.count));

  return (
    <div className="hours">
      {rows.map(row => (
        <div className="hour" key={row.id} title={`${row.id}:00 — ${row.count} зак., ${money(row.revenue)}`}>
          <div className="hour-bar" style={{ height: `${Math.max(6, (row.count / peak) * 100)}%` }} />
          <span className="num">{row.id}</span>
        </div>
      ))}
    </div>
  );
}

function RowTable({
  title,
  header,
  rows,
  amountTitle = "Сумма"
}: {
  title: string;
  header: string;
  rows: Row[];
  amountTitle?: string;
}) {
  return (
    <section className="panel">
      <header>
        <h2>{title}</h2>
      </header>

      {rows.length === 0 ? (
        <Empty>Нет данных за период.</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{header}</th>
                <th>Кол-во</th>
                <th>{amountTitle}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className="num">{row.count}</td>
                  <td className="num">{money(row.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
